import { NetworkStatus, useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Box, Stack } from "@mui/material";
import React from "react";
import { Outlet, useMatch, useNavigate } from "react-router-dom";
import { useUpdateEffect } from "react-use";
import { Agreement, PermissionName } from "../../../../clients/graphqlTypes.js";
import { useFeature } from "../../../../common/FeatureFlags.js";
import { useUserData } from "../../../auth/useUserData.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../../commons/layout/Page.js";
import { PageContainer } from "../../../commons/layout/PageContainer.js";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../../commons/modals/ConfirmProcess.js";
import { QUOTE_TEMPLATE_ITEM_TYPES } from "../../../constants.js";
import { DocDataGrid } from "../../../features/doc-data-grid/DocDataGrid.js";
import { createDocDataGridInputCell } from "../../../features/doc-data-grid/DocDataGridInputCell.js";
import { createDocDataGridItemCell } from "../../../features/doc-data-grid/DocDataGridItemCell.js";
import { DocDataGridSelectionContext } from "../../../features/doc-data-grid/DocDataGridSelectionContext.js";
import { useTemplateColumns } from "../../../features/templates/quote/useTemplateColumns.js";
import { useBatchMoveTreeItems } from "../../../features/doc-data-grid/useBatchMoveTreeItems.js";
import { useDocDataGridRowMutations } from "../../../features/doc-data-grid/useDocDataGridRowMutations.js";
import { useMoveTreeItem } from "../../../trees-virtual/hooks/useMoveTreeItem.js";
import { useDocDataGridRows } from "../../../features/doc-data-grid/useDocDataGridRows.js";
import { ProfitabilityBox } from "../../../features/doc-items/boxes/ProfitabilityBox.js";
import { rootItemHasCalculationConstraints } from "../../../features/doc-items/constraints.js";
import { isTreePreviewItemVisible } from "../../../features/doc-items/helpers.js";
import { getValidMoveTargets } from "../../../features/doc-items/tree-helpers.js";
import {
  allowHaveTemplateChildren,
  shouldRenderTemplateCreateInput,
} from "../../../features/templates/helpers.js";
import { CreateQuoteTemplateProcessRef } from "../../../features/templates/CreateQuoteTemplateModal.js";
import { TemplatesQuotePriceSummaryBox } from "../../../features/templates/quote/TemplatesQuotePriceSummaryBox.js";
import { TemplatesQuoteTimeSummaryBox } from "../../../features/templates/quote/TemplatesQuoteTimeSummaryBox.js";
import { createTreeItemInput } from "../../../features/templates/quote/trees.js";
import { useCardExpandedStore } from "../../../features/users/useCardExpandedStore.js";
import { useDataGridStateStore } from "../../../features/users/useDataGridStateStore.js";
import { ROOT_ITEM_ID } from "../../../trees-virtual/helpers.js";
import { useEnrichExpandedStoreWithItemIds } from "../../../trees-virtual/hooks/useEnrichExpandedStoreWithItemIds.js";
import { useEnrichExpandedStoreWithParentPathIds } from "../../../trees-virtual/hooks/useEnrichExpandedStoreWithParentPathIds.js";
import { useExpandedStoreWithLocalStorage } from "../../../trees-virtual/hooks/useExpandedStore.js";
import { TreeViewMode } from "../../../trees-virtual/types.js";
import { TreeToggleAllExpandedButton } from "../../../trees/components/TreeToggleButton.js";
import {
  TemplateQuoteDataGrid_ItemFragment,
  TemplateQuoteDataGrid_ItemFragmentDoc,
  useTemplateQuoteDataGridQuery,
} from "./TemplateQuoteDataGrid.generated.js";
import { useCreateTemplateItems } from "../../../features/templates/quote/useCreateTemplateItems.js";
import { useDocDataGridShell } from "../../../features/doc-data-grid/useDocDataGridShell.js";

interface Props {
  docId: string;
  pathToDocPage: string;
  submenuItems: PageTopbarItem[];
  prefixBreadcrumbs: BreadcrumbItem[];
  pageSubHeader: React.ReactElement | null;
}

export const TemplateQuoteDataGrid = ({
  docId,
  pathToDocPage,
  submenuItems,
  prefixBreadcrumbs,
  pageSubHeader,
}: Props) => {
  const match = useMatch(`${pathToDocPage}/items/:itemId`);
  const selectedItemId = match?.params?.itemId ?? null;
  const navigate = useNavigate();

  const viewer = useUserData().currentUser!;
  const isClientVisibilityEnabled = useFeature("ClientVisibility");

  const {
    rowSelectionModel,
    setRowSelectionModel,
    checkedItemIds,
    isItemChecked,
    clearChecked,
    additionalItemInput,
    setAdditionalItemInput,
    onAdditionalItemInputChange,
  } = useDocDataGridShell();

  const expandedStore = useExpandedStoreWithLocalStorage(
    "quote-template",
    docId,
    selectedItemId
  );

  const client = useApolloClient();
  const query = useTemplateQuoteDataGridQuery({
    client,
    variables: { id: docId, expandedItemIds: expandedStore.expandedItemIds },
  });

  const doc = getDataOrNull(query.data?.quoteTemplateLatest)?.quoteTemplate;
  const docTitle = doc?.title ?? "";

  const viewerPermissions: PermissionName[] = viewer.organisationPermissions;
  const agreement: Agreement = "NONE";

  const isOwnTemplate =
    doc?.owningSystemOrganisationId === viewer.organisation.id;
  const canEdit = isOwnTemplate;

  const navigateToItem = React.useCallback(
    (id: string) => navigate(`${pathToDocPage}/items/${id}`, { replace: true }),
    [navigate, pathToDocPage]
  );
  const navigateToDocPage = React.useCallback(
    () => navigate(pathToDocPage, { replace: true }),
    [navigate, pathToDocPage]
  );

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback(
    (props: ConfirmModalProps) =>
      confirmProcessRef.current!.startConfirmProcess(props),
    []
  );

  const createQuoteTemplateRef =
    React.useRef<CreateQuoteTemplateProcessRef | null>(null);

  const {
    createItem,
    createItemByType,
    createItemFromTemplate,
    createItemsFromTemplates,
    createItemsWithProducts,
    createItemWithLinkedTemplate,
    pasteItem,
    isAllowedToPasteItem,
  } = useCreateTemplateItems({
    templateId: docId,
    createQuoteTemplateRef,
    expandedItemIds: expandedStore.expandedItemIds,
  });

  const [batchMove] = useBatchMoveTreeItems({ projectId: null, docId });

  const [moveTreeItem, isMoveLoading] = useMoveTreeItem({
    projectId: null,
    docId,
  });

  const [filteredTreeItems, rootItem, allDocItems] = React.useMemo(() => {
    if (!doc) return [[], undefined, []];
    const allDocItems = doc.items;
    const rootItem = allDocItems?.find(item => item.isRootItem);
    const treeItems = allDocItems
      .filter(item => !item.isAnyParentItemEliminated)
      .map(item =>
        item.isRootItem ? { ...item, parentId: ROOT_ITEM_ID } : item
      );
    return [treeItems, rootItem, allDocItems];
  }, [doc]);

  const getValidItemMoveTargets = React.useCallback(
    async (itemIds: string[]) =>
      getValidMoveTargets(allDocItems, itemIds, QUOTE_TEMPLATE_ITEM_TYPES),
    [allDocItems]
  );

  const { isSavingRow, handleMove, processRowUpdate } =
    useDocDataGridRowMutations<TemplateQuoteDataGrid_ItemFragment>({
      allDocItems,
      batchMove,
      clearChecked,
      fragment: TemplateQuoteDataGrid_ItemFragmentDoc,
      fragmentName: "TemplateQuoteDataGrid_Item",
    });

  const handleMoveRef = React.useRef<
    (targetItemId: string, itemIds: string[]) => Promise<void>
  >(async () => {});
  handleMoveRef.current = handleMove;

  const ItemComponent = React.useMemo(() => {
    if (doc?.contractType && doc?.rootItem.id) {
      return createDocDataGridItemCell<TemplateQuoteDataGrid_ItemFragment>({
        pathToDocPage,
        projectId: null,
        doc: {
          id: docId,
          type: "TEMPLATE",
          rootItemId: doc.rootItem.id,
          contractType: doc.contractType,
          viewerPermissions,
          owningSystemOrganisationId: doc.owningSystemOrganisationId,
        },
        startConfirmProcess,
        navigateToItem,
        expandedStore,
        pasteItem,
        isAllowedToPasteItem,
        onAdditionalItemInputChange,
        getValidMoveTargets: getValidItemMoveTargets,
        onMoveItems: (...args) => handleMoveRef.current(...args),
      });
    }
    return () => null;
  }, [
    doc?.contractType,
    doc?.rootItem.id,
    doc?.owningSystemOrganisationId,
    pathToDocPage,
    docId,
    viewerPermissions,
    startConfirmProcess,
    navigateToItem,
    expandedStore,
    pasteItem,
    isAllowedToPasteItem,
    onAdditionalItemInputChange,
    getValidItemMoveTargets,
  ]);

  const InputComponent = React.useMemo(
    () =>
      createDocDataGridInputCell<TemplateQuoteDataGrid_ItemFragment>({
        viewMode: TreeViewMode.DataGrid,
        doc: {
          id: docId,
          docType: "TEMPLATE",
          templateIsDraft: !doc?.hasAnyPublishedVersion,
        },
        documentItemTypes: QUOTE_TEMPLATE_ITEM_TYPES,
        createItem,
        createItemByType,
        createItemFromTemplate,
        createItemsFromTemplates,
        createItemsWithProducts,
        pasteItem,
        isAllowedToPasteItem,
      }),
    [
      docId,
      doc?.hasAnyPublishedVersion,
      createItem,
      createItemByType,
      createItemFromTemplate,
      createItemsFromTemplates,
      createItemsWithProducts,
      isAllowedToPasteItem,
      pasteItem,
    ]
  );

  const TreeItemInput = React.useMemo(
    () =>
      createTreeItemInput({
        docId,
        isDraft: !doc?.hasAnyPublishedVersion,
        createQuoteTemplateRef,
        createItem,
        createItemByType,
        createItemFromTemplate,
        createItemsFromTemplates,
        createItemsWithProducts,
        createItemWithLinkedTemplate,
        pasteItem,
        isAllowedToPasteItem,
      }),
    [
      docId,
      doc?.hasAnyPublishedVersion,
      createItem,
      createItemByType,
      createItemFromTemplate,
      createItemsFromTemplates,
      createItemsWithProducts,
      createItemWithLinkedTemplate,
      pasteItem,
      isAllowedToPasteItem,
    ]
  );

  const showExpandButton = React.useCallback(
    (item: TemplateQuoteDataGrid_ItemFragment) =>
      allowHaveTemplateChildren(item) && item.hasChildren,
    []
  );

  useUpdateEffect(() => {
    setAdditionalItemInput(null);
  }, [selectedItemId, allDocItems]);

  React.useEffect(() => {
    if (
      !query.loading &&
      selectedItemId &&
      !filteredTreeItems.some(item => item.id === selectedItemId) &&
      selectedItemId !== rootItem?.id
    ) {
      navigateToDocPage();
    }
  }, [
    selectedItemId,
    filteredTreeItems,
    rootItem,
    query.loading,
    navigateToDocPage,
  ]);

  useEnrichExpandedStoreWithParentPathIds(
    expandedStore,
    allDocItems,
    selectedItemId,
    !query.loading && !query.error
  );
  useEnrichExpandedStoreWithItemIds(
    expandedStore,
    allDocItems,
    !query.loading &&
      !query.error &&
      query.networkStatus === NetworkStatus.ready,
    showExpandButton
  );

  const filterItem = React.useCallback(
    (item: TemplateQuoteDataGrid_ItemFragment) =>
      isTreePreviewItemVisible(true, item, allDocItems, doc?.isBinding ?? true),
    [allDocItems, doc?.isBinding]
  );

  const selectedItem = selectedItemId
    ? (filteredTreeItems.find(i => i.id === selectedItemId) ?? null)
    : null;

  const rows = useDocDataGridRows(
    filteredTreeItems,
    rootItem,
    selectedItem,
    additionalItemInput,
    canEdit,
    QUOTE_TEMPLATE_ITEM_TYPES,
    isClientVisibilityEnabled ? filterItem : undefined,
    shouldRenderTemplateCreateInput
  );

  const stateStore = useDataGridStateStore("TemplateQuoteDataGrid");

  const timeStore = useCardExpandedStore("template-quote-time-summary", false);
  const priceStore = useCardExpandedStore(
    "template-quote-price-summary",
    false
  );
  const profitabilityStore = useCardExpandedStore(
    "template-quote-profitability",
    false
  );

  const { columns, initialState } = useTemplateColumns({
    docId,
    canEdit,
    expandedItemIds: expandedStore.expandedItemIds,
    viewerIsContractor: true,
  });

  const enableDragging = canEdit;

  return (
    <Page
      title={docTitle}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs}
      subHeader={pageSubHeader}
      headerType="template"
    >
      {doc && (
        <PageContainer $display="flex" $noPadding $fullHeight>
          <Stack
            direction="column"
            spacing={2}
            sx={{
              py: 2,
              flex: 1,
              // minHeight/minWidth 0: lets children with `flex: 1` and
              // `min-height: <px>` actually shrink below their content size.
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{ width: "100%", alignItems: "center", px: 2 }}
            >
              <TreeToggleAllExpandedButton
                areAllItemsExpanded={expandedStore.areAllItemsExpanded}
                setAllItemsExpanded={expandedStore.setAllItemsExpanded}
              />
            </Stack>

            <Box
              sx={{
                flex: "1 1 auto",
                minHeight: 300,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                px: 2,
              }}
            >
              <DocDataGridSelectionContext.Provider
                value={{ checkedItemIds, isItemChecked, clearChecked }}
              >
                <DocDataGrid<TemplateQuoteDataGrid_ItemFragment>
                  rows={rows}
                  moveTreeItem={enableDragging ? moveTreeItem : undefined}
                  isMoveLoading={isMoveLoading}
                  expandedRowIds={expandedStore.expandedItemIds}
                  onExpandedRowIdChange={expandedStore.setItemExpanded}
                  isItemExpanded={expandedStore.isItemExpanded}
                  showExpandButton={showExpandButton}
                  selectedItemId={selectedItemId}
                  documentItemTypes={QUOTE_TEMPLATE_ITEM_TYPES}
                  ItemComponent={ItemComponent}
                  InputComponent={InputComponent}
                  docAgreement={agreement}
                  columns={columns}
                  processRowUpdate={processRowUpdate}
                  stateStore={stateStore}
                  initialState={initialState}
                  isSavingRow={isSavingRow}
                  enableMultiSelect={canEdit}
                  rowSelectionModel={rowSelectionModel}
                  onRowSelectionModelChange={setRowSelectionModel}
                />
              </DocDataGridSelectionContext.Provider>
            </Box>

            {rootItem &&
              rootItemHasCalculationConstraints.includes(rootItem.type) &&
              doc.proposedCalculation && (
                <Stack direction="column" spacing={2} sx={{ px: 2 }}>
                  <TemplatesQuoteTimeSummaryBox
                    calculated={doc.proposedCalculation}
                    isExpanded={timeStore.isExpanded}
                    onExpandedChange={timeStore.setIsExpanded}
                  />
                  <TemplatesQuotePriceSummaryBox
                    docId={doc.id}
                    discountPercentage={doc.discountPercentage}
                    calculated={doc.proposedCalculation}
                    isReadOnly={!canEdit}
                    isExpanded={priceStore.isExpanded}
                    onExpandedChange={priceStore.setIsExpanded}
                  />
                  <ProfitabilityBox
                    calculated={doc.proposedCalculation}
                    isExpanded={profitabilityStore.isExpanded}
                    onExpandedChange={profitabilityStore.setIsExpanded}
                  />
                </Stack>
              )}
          </Stack>
        </PageContainer>
      )}
      <ConfirmProcess ref={confirmProcessRef} />
      <Outlet
        context={{
          expandedItemIds: expandedStore.expandedItemIds,
          CreateChildComponent: TreeItemInput,
          pasteItem,
          isAllowedToPasteItem,
          viewerDecisionRole: undefined,
        }}
      />
    </Page>
  );
};
